import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { Button } from './Button';
import styles from './ErrorPage.module.css';

/**
 * Full-screen error boundary rendered by the router when a route throws.
 * Shows a friendly summary with the raw error expandable below it, plus
 * navigation escape hatches (back / home). `reset` clears the boundary so
 * navigating away re-renders the destination route cleanly.
 */
export function ErrorPage({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  const message = error instanceof Error ? error.message : String(error);
  const detail = error instanceof Error ? (error.stack ?? message) : message;

  const goBack = () => {
    reset();
    router.history.back();
  };

  const goHome = () => {
    reset();
    void router.navigate({ to: '/' });
  };

  return (
    <div className={styles.screen} role="alert">
      <div className={styles.panel}>
        <p className={styles.code} aria-hidden="true">
          ── probe fault ──
        </p>
        <h1 className={styles.title}>Something went wrong</h1>
        <p className={styles.message}>{message}</p>

        <div className={styles.actions}>
          <Button onClick={goBack} variant="secondary">
            ← Go back
          </Button>
          <Button onClick={goHome}>Return home</Button>
        </div>

        <details className={styles.details}>
          <summary className={styles.summary}>Error details</summary>
          <pre className={styles.stack}>{detail}</pre>
        </details>
      </div>
    </div>
  );
}
